import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import CountryChips from "@/components/CountryChips";
import { useCountry } from "@/lib/CountryContext";
import { formatPrice, billingSuffix } from "@/lib/currency";
import { COUNTRIES } from "@/i18n/countries";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Check, X, Eye, ExternalLink, RotateCcw, Plus, ArrowRight } from "lucide-react";

const STATUS_STYLE = {
    Live: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Pending Review": "bg-gold-500/10 text-gold-700 border-gold-200",
    Draft: "bg-navy-50 text-navy-500 border-navy-100",
    Rejected: "bg-red-50 text-red-700 border-red-200",
};

export default function Products() {
    const [data, setData] = useState({ groups: [], deals: [], broker_products: [] });
    const [viewing, setViewing] = useState(null);
    const [adminNotes, setAdminNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("deals");
    const [filterCountry, setFilterCountry] = useState("all");
    const navigate = useNavigate();
    const { country } = useCountry();

    // A row matches a country filter if:
    //   • filter is "all", OR
    //   • the row's `country_codes` is empty/null (global — appears in every market), OR
    //   • the row's `country_codes` contains the filter code.
    const matches = (row) => {
        if (filterCountry === "all") return true;
        const codes = row.country_codes;
        if (!codes || codes.length === 0) return true;
        return codes.includes(filterCountry);
    };

    const filteredDeals = useMemo(
        () => data.deals.filter(matches),
        [data.deals, filterCountry]
    );
    const filteredGroups = useMemo(
        () => data.groups.filter(matches),
        [data.groups, filterCountry]
    );

    const load = () =>
        api
            .get("/admin/products")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)));

    useEffect(() => {
        load();
    }, []);

    // If there are broker uploads awaiting review, auto-open that tab
    // on first load. Users can freely switch afterwards.
    // const [initialTabSet, setInitialTabSet] = useState(false);
    // useEffect(() => {
    //     if (!initialTabSet && data.broker_products.length > 0) {
    //         const hasPending = data.broker_products.some(
    //             (p) => p.status === "Pending Review"
    //         );
    //         if (hasPending) setTab("broker");
    //         setInitialTabSet(true);
    //     }
    // }, [data.broker_products, initialTabSet]);

    const openView = (p) => {
        setViewing(p);
        setAdminNotes(p.admin_notes || "");
    };

    const decide = async (id, status) => {
        setBusy(true);
        try {
            const { data: updated } = await api.patch(
                `/admin/broker-products/${id}`,
                { status, admin_notes: adminNotes }
            );
            setData((d) => ({
                ...d,
                broker_products: d.broker_products.map((p) =>
                    p.id === id ? updated : p
                ),
            }));
            setViewing(updated);
            toast.success(`Product marked ${status}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const pending = data.broker_products.filter(
        (p) => p.status === "Pending Review"
    );

    return (
        <>
            <PageHeader
                eyebrow="Catalogue"
                title="Products & groups"
                description="Every product group, live deal and broker upload on the Perx platform."
                testid="admin-products-header"
                action={
                    <div className="flex items-center gap-3 flex-wrap">
                        {pending.length > 0 && (
                            <Badge
                                data-testid="admin-pending-review-badge"
                                className="bg-gold-500 text-navy-900 border-gold-200 border text-xs px-3 py-1 uppercase tracking-widest"
                            >
                                {pending.length} awaiting review
                            </Badge>
                        )}
                        {tab === "groups" ? (
                            <Button
                                data-testid="admin-new-group-btn"
                                onClick={() => navigate("/admin/products/groups/new")}
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                            >
                                <Plus size={16} className="mr-2" /> New group
                            </Button>
                        ) : (
                            <Button
                                data-testid="admin-new-product-btn"
                                onClick={() => navigate("/admin/products/deals/new")}
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                            >
                                <Plus size={16} className="mr-2" /> New product
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 lg:px-12 py-8">
                <Tabs
                    value={tab}
                    onValueChange={setTab}
                    className="w-full"
                >
                    <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                        <TabsList className="bg-white border border-navy-100 rounded-full h-11 p-1">
                            <TabsTrigger
                                value="deals"
                                data-testid="admin-tab-deals"
                                className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                            >
                                {filteredDeals.length} deals
                            </TabsTrigger>
                            <TabsTrigger
                                value="groups"
                                data-testid="admin-tab-groups"
                                className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                            >
                                {filteredGroups.length} groups
                            </TabsTrigger>
                            <TabsTrigger
                                value="broker"
                                data-testid="admin-tab-broker"
                                className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                            >
                                Broker uploads ({data.broker_products.length})
                                {pending.length > 0 && (
                                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-gold-500 text-navy-900 text-[10px] font-bold h-5 min-w-5 px-1.5">
                                        {pending.length}
                                    </span>
                                )}
                            </TabsTrigger>
                        </TabsList>

                        {tab !== "broker" && (
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase tracking-widest text-navy-500">
                                    Filter by market
                                </span>
                                <Select
                                    value={filterCountry}
                                    onValueChange={setFilterCountry}
                                >
                                    <SelectTrigger
                                        data-testid="admin-products-country-filter"
                                        className="h-10 min-w-[210px] bg-white border-navy-100 rounded-full pl-4"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem
                                            value="all"
                                            data-testid="admin-products-country-filter-all"
                                        >
                                            🌏 All markets
                                        </SelectItem>
                                        {COUNTRIES.map((c) => (
                                            <SelectItem
                                                key={c.code}
                                                value={c.code}
                                                data-testid={`admin-products-country-filter-${c.code}`}
                                            >
                                                {c.flag} {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {filterCountry !== "all" && (
                                    <button
                                        data-testid="admin-products-country-filter-clear"
                                        onClick={() => setFilterCountry("all")}
                                        className="text-[11px] uppercase tracking-widest text-gold-600 hover:text-gold-700 underline underline-offset-4"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <TabsContent value="deals">
                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                            <Table data-testid="admin-deals-table">
                                <TableHeader>
                                    <TableRow className="bg-cream hover:bg-cream">
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Deal</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Vendor</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Group</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Markets</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Price</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Savings</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Rating</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredDeals.map((d) => (
                                        <TableRow
                                            key={d.id}
                                            data-testid={`admin-deal-row-${d.id}`}
                                            onClick={() => navigate(`/admin/products/deals/${d.id}`)}
                                            className="cursor-pointer transition-colors hover:bg-cream/60 group"
                                        >
                                            <TableCell className="font-medium text-navy-700 group-hover:text-gold-700 transition-colors">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {d.title}
                                                    <ArrowRight
                                                        size={14}
                                                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                                    />
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-navy-500">{d.vendor}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <Badge className="bg-navy-50 text-navy-700 border-navy-100 border">
                                                        {d.group_id.replace("grp-", "")}
                                                    </Badge>
                                                    {(d.additional_group_ids || []).map((gid) => (
                                                        <Badge
                                                            key={gid}
                                                            data-testid={`deal-extra-group-${d.id}-${gid}`}
                                                            className="bg-white text-navy-500 border-navy-100 border font-normal"
                                                        >
                                                            +{gid.replace("grp-", "")}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <CountryChips
                                                    codes={d.country_codes}
                                                    testid={`deal-countries-${d.id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-navy-700">
                                                {formatPrice(d.price, d.is_paid_USD ? "US" : country)}
                                                {billingSuffix(d.billing_interval)}
                                            </TableCell>
                                            <TableCell className="text-gold-600 font-medium">{d.savings_pct}%</TableCell>
                                            <TableCell className="text-navy-500">★ {d.rating.toFixed(1)}</TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    testid={`deal-status-${d.id}`}
                                                    status={d.status || "active"}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="groups">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredGroups.map((g) => {
                                const active = (g.status || "active") === "active";
                                return (
                                    <div
                                        key={g.id}
                                        data-testid={`admin-group-card-${g.id}`}
                                        onClick={() => navigate(`/admin/products/groups/${g.id}`)}
                                        className={`group cursor-pointer rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lift ${
                                            active ? "" : "opacity-60"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div
                                                    className="text-xs uppercase tracking-[0.3em] mb-2"
                                                    style={{ color: g.accent }}
                                                >
                                                    {g.deal_count} live
                                                </div>
                                                <div className="font-display text-2xl text-navy-700 group-hover:text-gold-700 transition-colors inline-flex items-center gap-1.5">
                                                    {g.name}
                                                    <ArrowRight
                                                        size={14}
                                                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                                    />
                                                </div>
                                                <div className="text-navy-500 text-sm mt-1 line-clamp-1">
                                                    {g.tagline}
                                                </div>
                                            </div>
                                            <img
                                                src={g.image}
                                                alt=""
                                                className="h-16 w-16 object-contain flex-shrink-0"
                                            />
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-navy-100/70 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[11px] font-mono text-navy-500 truncate">
                                                    {g.id}
                                                </span>
                                                <CountryChips
                                                    codes={g.country_codes}
                                                    testid={`group-countries-${g.id}`}
                                                />
                                            </div>
                                            <StatusBadge
                                                testid={`group-status-${g.id}`}
                                                status={g.status || "active"}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabsContent>

                    <TabsContent value="broker">
                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                            <Table data-testid="admin-broker-products-table">
                                <TableHeader>
                                    <TableRow className="bg-cream hover:bg-cream">
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Product</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Vendor</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Category</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Retail → Member</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                        <TableHead className="text-right text-navy-700 uppercase text-xs tracking-[0.2em]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.broker_products.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center text-navy-500 py-10">
                                                No broker uploads yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data.broker_products.map((p) => (
                                        <BrokerRow
                                            key={p.id}
                                            product={p}
                                            onView={() => openView(p)}
                                            onApprove={() => decide(p.id, "Live")}
                                            onReject={() => decide(p.id, "Rejected")}
                                            busy={busy}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <ViewPortalDialog
                product={viewing}
                onClose={() => setViewing(null)}
                notes={adminNotes}
                setNotes={setAdminNotes}
                onDecide={decide}
                busy={busy}
            />
        </>
    );
}

function StatusBadge({ status, testid }) {
    const active = status === "active";
    return (
        <Badge
            data-testid={testid}
            className={
                (active
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200") +
                " border text-[10px] uppercase tracking-widest"
            }
        >
            <span
                className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${
                    active ? "bg-emerald-500" : "bg-red-500"
                }`}
            />
            {active ? "Live" : "Hidden"}
        </Badge>
    );
}

function BrokerRow({ product, onView, onApprove, onReject, busy }) {
    const p = product;
    const pending = p.status === "Pending Review";
    return (
        <TableRow data-testid={`admin-broker-row-${p.id}`}>
            <TableCell className="font-medium text-navy-700">
                {p.product}
                {p.submitted_by && (
                    <div className="text-[11px] text-navy-500 mt-0.5">by {p.submitted_by}</div>
                )}
            </TableCell>
            <TableCell className="text-navy-500">{p.vendor}</TableCell>
            <TableCell>
                <Badge className="bg-navy-50 text-navy-700 border-navy-100 border">
                    {p.category || "General"}
                </Badge>
            </TableCell>
            <TableCell className="text-navy-700 text-sm">
                {p.retail_price != null && p.actual_price != null ? (
                    <>
                        <span className="line-through text-navy-500 mr-1">${p.retail_price}</span>
                        <span className="font-semibold text-gold-700">${p.actual_price}</span>
                    </>
                ) : (
                    <span className="text-navy-500">—</span>
                )}
            </TableCell>
            <TableCell>
                <Badge
                    className={`${STATUS_STYLE[p.status] || "bg-navy-50 text-navy-500 border-navy-100"} border text-xs`}
                    data-testid={`admin-broker-status-${p.id}`}
                >
                    {p.status}
                </Badge>
            </TableCell>
            <TableCell className="text-right">
                <div className="inline-flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onView}
                        data-testid={`admin-broker-view-${p.id}`}
                        className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-8"
                    >
                        <Eye size={14} className="mr-1.5" /> View portal
                    </Button>
                    {pending && (
                        <>
                            <Button
                                size="sm"
                                onClick={onApprove}
                                disabled={busy}
                                data-testid={`admin-broker-approve-${p.id}`}
                                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                            >
                                <Check size={14} className="mr-1.5" /> Approve
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={onReject}
                                disabled={busy}
                                data-testid={`admin-broker-reject-${p.id}`}
                                className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-8"
                            >
                                <X size={14} className="mr-1.5" /> Reject
                            </Button>
                        </>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}

function ViewPortalDialog({ product, onClose, notes, setNotes, onDecide, busy }) {
    if (!product) return null;
    const p = product;
    const saving =
        p.retail_price != null && p.actual_price != null
            ? p.retail_price - p.actual_price
            : null;
    return (
        <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="admin-broker-view-dialog"
                className="bg-white max-w-2xl"
            >
                <DialogHeader>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                        Broker upload · {p.id}
                    </div>
                    <DialogTitle className="font-display text-2xl text-navy-700">
                        {p.product}
                    </DialogTitle>
                    <DialogDescription className="text-navy-500">
                        {p.vendor} · submitted by {p.submitted_by || "broker"}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid sm:grid-cols-2 gap-4 py-2">
                    <Field label="Category" value={p.category || "General"} />
                    <Field
                        label="Status"
                        value={
                            <Badge
                                className={`${STATUS_STYLE[p.status]} border text-xs`}
                            >
                                {p.status}
                            </Badge>
                        }
                    />
                    <Field
                        label="Retail price"
                        value={p.retail_price != null ? `$${p.retail_price}` : "—"}
                    />
                    <Field
                        label="Member price"
                        value={p.actual_price != null ? `$${p.actual_price}` : "—"}
                    />
                    {saving !== null && (
                        <Field
                            label="Member saves"
                            value={
                                <span className="text-gold-700 font-semibold">
                                    ${saving.toFixed(2)} (
                                    {Math.round((saving / p.retail_price) * 100)}% off)
                                </span>
                            }
                        />
                    )}
                    {p.website && (
                        <Field
                            label="Website"
                            value={
                                <a
                                    href={p.website}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-navy-700 hover:text-gold-600 inline-flex items-center gap-1"
                                    data-testid="admin-broker-view-website"
                                >
                                    {p.website.replace(/^https?:\/\//, "")}
                                    <ExternalLink size={12} />
                                </a>
                            }
                        />
                    )}
                </div>

                {p.description && (
                    <div className="rounded-lg bg-cream p-4 text-sm text-navy-700">
                        {p.description}
                    </div>
                )}

                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                        Admin notes
                    </div>
                    <Textarea
                        data-testid="admin-broker-notes"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add review notes for the broker (e.g. missing docs, pricing questions)…"
                        className="bg-white border-navy-100 focus-visible:ring-gold-500"
                    />
                </div>

                <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                    <div className="text-xs text-navy-500">
                        {p.reviewed_by
                            ? `Last reviewed by ${p.reviewed_by}`
                            : "Not yet reviewed"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {p.status !== "Pending Review" && (
                            <Button
                                variant="outline"
                                disabled={busy}
                                onClick={() => onDecide(p.id, "Pending Review")}
                                data-testid="admin-broker-dialog-reset"
                                className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-10"
                            >
                                <RotateCcw size={14} className="mr-1.5" /> Send back to review
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            disabled={busy || p.status === "Rejected"}
                            onClick={() => onDecide(p.id, "Rejected")}
                            data-testid="admin-broker-dialog-reject"
                            className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-10"
                        >
                            <X size={14} className="mr-1.5" /> Reject
                        </Button>
                        <Button
                            disabled={busy || p.status === "Live"}
                            onClick={() => onDecide(p.id, "Live")}
                            data-testid="admin-broker-dialog-approve"
                            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-6"
                        >
                            <Check size={14} className="mr-1.5" /> Approve & publish
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-navy-500 mb-1">
                {label}
            </div>
            <div className="text-sm text-navy-700 font-medium">{value}</div>
        </div>
    );
}
